import React, { useContext, useEffect, useState } from 'react';

// eslint-disable-next-line @typescript-eslint/no-unused-vars
import { AjaxContext, AppContext, formatListData, ModalContext, UserContext } from 'commons-ui';
import { ACTION, MISCELLANEOUS_RESOURCES, PAGE_NAMES } from 'constant';
import _find from 'lodash/find';
import { SelectFilterContent } from '../submodule/selectFilter';

const useLegalDetails = () => {
	const [showLoader, setShowLoader] = useState<boolean>(false);
	const { setModalConfig } = useContext(ModalContext);
	const {
		app: { appName },
		showAlert
	} = useContext(AppContext);
	const { postData } = useContext(AjaxContext);
	const { permissionsConfig } = useContext(UserContext);
	const [legalDetails, setLegalDetails] = useState<{ rowCount: number; data: any[] }>({ rowCount: 0, data: [] });
	const [filters, setFilters] = useState<{ username: string } | null>(null);
	const [forceUpdate, setForceUpdate] = useState<boolean>(false);
	const [pageNumber, setPageNumber] = useState<{ currPage: number; nextPage: number | null }>({ currPage: 1, nextPage: null });

	const handleFilterFormClose = () => {
		setModalConfig({ isOpen: false });
	};

	const handleFilterSubmit = (submittedFilters: any) => {
		const { name }: { name: string } = submittedFilters;
		if (name && name.trim() !== '') {
			const username = name.split(' ').join('%');
			setFilters({ username });
		} else {
			setFilters(null);
		}
	};

	const handleDownloadLegalDetails = async () => {
		try {
			if (!(legalDetails?.data && legalDetails?.data?.length > 0)) return;

			setShowLoader(true);

			const data = legalDetails?.data;
			const allPossibleKeys = [
				'dt',
				'zupeeuserid',
				'name',
				'gross_deposit_amount',
				'gross_withdrawal_amount',
				'cash_game_gmv',
				'winnings',
				'tds_cashback',
				'def_amount',
				'last_custom_state',
				'last_custom_city',
				'signup_state',
				'last_3m_gross_deposit_amount',
				'last_3m_deposit_count',
				'last_3m_withdrawal_amount',
				'net_loss_on_deposit'
			];

			const normalizedData = data.map((item) => {
				const normalizedItem = { ...item };
				allPossibleKeys.forEach((key) => {
					if (!(key in normalizedItem)) {
						normalizedItem[key] = '';
					}
				});
				return normalizedItem;
			});

			const headers = allPossibleKeys.join(',');
			const rows = normalizedData.map((item) => {
				return allPossibleKeys
					.map((key) => {
						let value = item[key];
						if (['dt'].includes(key)) {
							value = new Date(value).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' });
						}
						if (value === null || value === undefined) {
							return '';
						}
						return `"${String(value).replace(/"/g, '""')}"`;
					})
					.join(',');
			});

			const csvContent = [headers, ...rows].join('\n');
			const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
			const url = window.URL.createObjectURL(blob);
			const link = document.createElement('a');
			const timestamp = new Date().toISOString().split('T')[0];
			link.setAttribute('download', `Legal-details-data_${timestamp}.csv`);
			link.href = url;
			document.body.appendChild(link);
			link.click();
			document.body.removeChild(link);
			window.URL.revokeObjectURL(url);
		} catch (error: any) {
			showAlert('Error while downloading legal details data!', { type: 'error' });
		} finally {
			setShowLoader(false);
		}
	};

	const fetchAllLegalDetails = async (params: any) => {
		try {
			if (!params?.username) {
				return;
			}
			setShowLoader(true);
			const { username } = params;
			const requestBody: any = { username };
			const permission = _find(permissionsConfig, { actionName: ACTION.getLegalDetailsOfUser });
			const response: any = await postData({
				endpoint: `/dashboard/getLegalDetails/${PAGE_NAMES.miscellaneous}-${MISCELLANEOUS_RESOURCES.legalDetails}-getLegalDetailsOfUser`,
				queryParams: {
					appName
				},
				body: requestBody,
				customHeaders: {
					isMasked: permission?.isMasked || false
				}
			});

			if (response.data?.success) {
				setLegalDetails({ rowCount: response.data.data.length, data: formatListData(response.data.data, 'id') });
			} else {
				setLegalDetails({ rowCount: 0, data: [] });
				showAlert('No data found for the given username.', { type: 'error' });
			}
			setPageNumber({ currPage: pageNumber.currPage, nextPage: pageNumber.currPage + 1 }); // TODO: currPage: pageNumber.currPage + 1, nextPage: pageNumber.currPage + 2
			setForceUpdate(true);
		} catch (error) {
			showAlert('Error in fetching legal details data.', { type: 'error' });
		} finally {
			setShowLoader(false);
		}
	};

	const handleFilterModalOpen = () => {
		setModalConfig({
			isOpen: true,
			header: 'Legal Details Filter',
			content: <SelectFilterContent handleFilterSubmit={handleFilterSubmit} onClose={handleFilterFormClose} />
		});
	};

	const getAllLegalDetailsData = () => {
		setForceUpdate(false);
		return legalDetails;
	};

	useEffect(() => {
		handleFilterModalOpen();
	}, []);

	useEffect(() => {
		if (filters !== null && filters !== undefined) {
			fetchAllLegalDetails(filters);
		}
	}, [filters]);

	return {
		forceUpdate,
		showLoader,
		handleDownloadLegalDetails,
		fetchAllLegalDetails,
		handleFilterModalOpen,
		getAllLegalDetailsData
	};
};

export default useLegalDetails;
